import { useState } from "react";
import { Consultation } from "@/types";

interface DashboardFiltersProps {
  consultations: Consultation[];
  filters: {
    urgency: string;
    priority: string;
    status: string;
    search: string;
  };
  onFilterChange: (key: string, value: string) => void;
  onResetFilters: () => void;
  getUrgencyLevel: (departureDate: string) => string;
}

export function DashboardFilters({
  consultations,
  filters,
  onFilterChange,
  onResetFilters,
  getUrgencyLevel,
}: DashboardFiltersProps) {
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const isAnyFilterActive =
    filters.urgency !== "all" ||
    filters.priority !== "all" ||
    filters.status !== "all" ||
    filters.search !== "";

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.urgency !== "all") count++;
    if (filters.priority !== "all") count++;
    if (filters.status !== "all") count++;
    return count;
  };

  const toggleFilter = (filterType: string, value: string) => {
    const currentValue = filters[filterType as keyof typeof filters];
    onFilterChange(filterType, currentValue === value ? "all" : value);
  };

  const getCounts = () => {
    return {
      all: consultations.length,
      overdue: consultations.filter(
        (c) => getUrgencyLevel(c.departureDate) === "overdue",
      ).length,
      urgent: consultations.filter(
        (c) => getUrgencyLevel(c.departureDate) === "urgent",
      ).length,
      soon: consultations.filter(
        (c) => getUrgencyLevel(c.departureDate) === "soon",
      ).length,
      normal: consultations.filter(
        (c) => getUrgencyLevel(c.departureDate) === "normal",
      ).length,
      highPriority: consultations.filter((c) => c.priority === "high").length,
      pending: consultations.filter((c) => c.formsStatus.pending > 0).length,
      underReview: consultations.filter(
        (c) => (c.formsStatus.underReview || c.formsStatus.under_review) > 0,
      ).length,
    };
  };

  const counts = getCounts();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 mb-6">
      <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200">
        {/* Filter Chips - Horizontal Layout */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hidden sm:flex items-center gap-2 text-base text-gray-700 font-semibold">
            <svg
              className="w-5 h-5 text-teal-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span>Show me:</span>
          </div>

          <button
            onClick={onResetFilters}
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
              filters.status === "all" &&
              filters.urgency === "all" &&
              filters.priority === "all"
                ? "bg-teal-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All Consultations
          </button>

          <button
            onClick={() => toggleFilter("urgency", "overdue")}
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 ${
              filters.urgency === "overdue"
                ? "bg-error text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <span>Overdue</span>
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
              {counts.overdue}
            </span>
          </button>

          <button
            onClick={() => toggleFilter("urgency", "urgent")}
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 ${
              filters.urgency === "urgent"
                ? "bg-warning text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <span>Urgent</span>
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
              {counts.urgent}
            </span>
          </button>

        </div>
      </div>
    </div>
  );
}
